// src/pages/PurchaseOrders.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
  limit,
  startAt,
  endAt,
} from "firebase/firestore";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { db, auth } from "../services/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { createPO } from "../services/inventoryApi";

/** Utilitaire debounce */
function useDebounced(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

/** Ligne d’édition avec recherche par code-barres ou nom */
function POItemRow({ value, onChange, onRemove, index }) {
  const [term, setTerm] = useState("");
  const debounced = useDebounced(term, 220);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef(null);

  // Fermer dropdown quand on clique à l’extérieur
  useEffect(() => {
    const fn = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setSuggestions([]);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  // Recherche nom/code-barres
  useEffect(() => {
    let alive = true;
    (async () => {
      const t = debounced.trim();
      if (!t) {
        if (alive) setSuggestions([]);
        return;
      }
      setLoading(true);
      try {
        // 1) Essai code-barres exact
        const byBarcodeQ = query(
          collection(db, "itemsEK"),
          where("barcode", "==", t),
          limit(1)
        );
        const bSnap = await getDocs(byBarcodeQ);
        if (!alive) return;
        if (!bSnap.empty) {
          const d = bSnap.docs[0];
          const it = d.data();
          setSuggestions([
            {
              id: d.id,
              name: it.name || it.title || "Article",
              barcode: it.barcode || "",
              price: Number(it.price || 0),
              cost: Number(it.cost || 0),
            },
          ]);
          setActiveIdx(0);
          return;
        }

        // 2) Sinon: recherche par nameLower (préfixe)
        const lower = t.toLowerCase();
        const byNameQ = query(
          collection(db, "itemsEK"),
          orderBy("nameLower"),
          startAt(lower),
          endAt(lower + "\uf8ff"),
          limit(10)
        );
        const nSnap = await getDocs(byNameQ);
        if (!alive) return;
        const arr = [];
        nSnap.forEach((d) => {
          const it = d.data();
          arr.push({
            id: d.id,
            name: it.name || it.title || "Article",
            barcode: it.barcode || "",
            price: Number(it.price || 0),
            cost: Number(it.cost || 0),
          });
        });
        setSuggestions(arr);
        setActiveIdx(arr.length ? 0 : -1);
      } catch (e) {
        console.error(e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [debounced]);

  function pickSuggestion(s) {
    onChange({
      ...value,
      itemId: s.id,
      name: s.name,
      unitCost: value.unitCost ?? s.cost ?? 0,
    });
    setTerm(s.name);
    setSuggestions([]);
  }

  function onKeyDown(e) {
    if (!suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIdx >= 0) pickSuggestion(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      setSuggestions([]);
    }
  }

  const lineSubtotal = useMemo(
    () => (Number(value.qty || 0) * Number(value.unitCost || 0)) || 0,
    [value.qty, value.unitCost]
  );

  return (
    <div ref={wrapRef} className="row g-2 align-items-center position-relative" style={{ zIndex: 5 }}>
      <div className="col-12 col-lg-6">
        <div className="position-relative">
          <input
            className="form-control"
            placeholder="Scanner un code-barres ou taper un nom…"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onFocus={() => term && setSuggestions((s) => s)}
            onKeyDown={onKeyDown}
            autoComplete="off"
          />
          {loading && (
            <div
              className="spinner-border spinner-border-sm text-muted position-absolute"
              style={{ right: 10, top: 10 }}
            />
          )}

          {suggestions.length > 0 && (
            <div
              className="list-group shadow-sm position-absolute w-100 mt-1"
              style={{ maxHeight: 260, overflowY: "auto" }}
            >
              {suggestions.map((s, i) => (
                <button
                  type="button"
                  key={s.id}
                  className={
                    "list-group-item list-group-item-action d-flex justify-content-between align-items-center" +
                    (i === activeIdx ? " active" : "")
                  }
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => pickSuggestion(s)}
                >
                  <div>
                    <div className="fw-semibold">{s.name}</div>
                    <small className="text-muted">
                      {s.barcode ? `CB: ${s.barcode}` : "—"} · Prix: {s.price?.toLocaleString()} · Coût: {s.cost?.toLocaleString()}
                    </small>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {value.itemId && (
          <div className="form-text">
            Article choisi : <code>{value.itemId}</code>
            {value.name ? <> — {value.name}</> : null}
          </div>
        )}
      </div>

      <div className="col-6 col-lg-2">
        <input
          type="number"
          min={1}
          className="form-control"
          value={value.qty}
          onChange={(e) => onChange({ ...value, qty: Math.max(1, Number(e.target.value || 1)) })}
          placeholder="Qté"
        />
      </div>

      <div className="col-6 col-lg-2">
        <input
          type="number"
          min={0}
          className="form-control"
          value={value.unitCost ?? ""}
          onChange={(e) => onChange({ ...value, unitCost: Math.max(0, Number(e.target.value || 0)) })}
          placeholder="Coût unitaire"
        />
        <div className="form-text">
          Sous-total: <strong>{lineSubtotal.toLocaleString()}</strong>
        </div>
      </div>

      <div className="col-12 col-lg-2 d-flex gap-2">
        {index === 0 ? (
          <button
            type="button"
            className="btn btn-outline-secondary w-100"
            onClick={() => onChange({ itemId: "", name: "", qty: 1, unitCost: 0 })}
          >
            Effacer
          </button>
        ) : (
          <button type="button" className="btn btn-outline-danger w-100" onClick={onRemove}>
            Retirer
          </button>
        )}
      </div>
    </div>
  );
}

export default function PurchaseOrders() {
  const [creating, setCreating] = useState(false);
  const [lines, setLines] = useState([{ itemId: "", name: "", qty: 1, unitCost: 0 }]);
  const [supplier, setSupplier] = useState("");

  // Charger la liste des PO
  useEffect(() => {
    (async () => {
      const snap = await getDocs(
        query(collection(db, "purchaseOrders"), orderBy("createdAt", "desc"))
      );
      const arr = [];
      snap.forEach((d) => arr.push({ id: d.id, ...d.data() }));
      setPos(arr);
    })();
  }, []);

  function setLineAt(i, newVal) {
    setLines((p) => p.map((l, idx) => (idx === i ? newVal : l)));
  }
  function addLine() {
    setLines((p) => [...p, { itemId: "", name: "", qty: 1, unitCost: 0 }]);
  }
  function removeLine(i) {
    setLines((p) => p.filter((_, idx) => idx !== i));
  }

  const validLines = useMemo(
    () => lines.filter((l) => l.itemId && Number(l.qty) > 0),
    [lines]
  );

  const totalAmount = useMemo(
    () => validLines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unitCost || 0), 0),
    [validLines]
  );

  async function onCreate() {
    if (!validLines.length) {
      alert("Ajoute au moins 1 ligne avec un article sélectionné.");
      return;
    }
    if (!supplier.trim()) {
      if (!window.confirm("Aucun fournisseur saisi. Continuer ?")) return;
    }
    setCreating(true);
    let user = auth.currentUser;
    if (!user) {
      await new Promise((res) =>
        onAuthStateChanged(auth, (u) => {
          user = u;
          res();
        })
      );
    }
    try {
      const { id } = await createPO({
        supplier,
        lines: validLines.map((l) => ({
          itemId: l.itemId,
          qty: Number(l.qty),
          unitCost: Number(l.unitCost || 0),
          name: l.name || undefined,
        })),
        createdBy: user?.uid || "anonymous",
        amount: totalAmount,
      });

      // 👉 après création, on enchaine directement sur la page "shipping" du PO
      navigate(`/admin/po/${id}/ship`);
    } catch (e) {
      alert(e.message);
    } finally {
      setCreating(false);
    }
  }
  const cardLinkStyle = ({ isActive }) =>
    `card text-center p-3 border-0 shadow-sm rounded-3 text-decoration-none ${
      isActive ? "bg-dark text-white" : "bg-light text-dark"
    }`;

  // Helpers d’affichage
  //   function getTotals(po) {
  //     const items = Array.isArray(po.items) ? po.items : [];
  //     const ordered = items.reduce((s, l) => s + Number(l?.qtyOrdered || 0), 0);
  //     const received = items.reduce((s, l) => s + Number(l?.qtyReceived || 0), 0);
  //     return { ordered, received, remain: Math.max(0, ordered - received) };
  //   }
  function badgeClass(s = "open") {
    const st = (s || "").toLowerCase();
    if (st === "closed") return "badge bg-success";
    if (st === "partial") return "badge bg-warning text-dark";
    return "badge bg-secondary";
    // (ton style: bg-light text-dark si tu préfères)
  }

  const navigate = useNavigate();
  const [pos, setPos] = useState([]);
  const [selected, setSelected] = useState(new Set());

  function toggle(id) {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }
  const allChecked = selected.size && selected.size === pos.length;
  function toggleAll() {
    setSelected(prev => {
      if (prev.size === pos.length) return new Set();
      return new Set(pos.map(p => p.id));
    });
  }

  function goShipMulti() {
    if (!selected.size) return;
    const ids = Array.from(selected).join(",");
    navigate(`/admin/ship/new?po=${encodeURIComponent(ids)}`);
  }

  return (
    <>
      <div className="container">
        <h3 className="mb-3">Purchase Orders</h3>
        <NavLink to="/dev/add-item" className={cardLinkStyle}>
          Commandes
        </NavLink>
        {/* Création de PO */}
        <div className="card p-3 mb-4">
          <h6>Créer un PO (scan ou recherche nom)</h6>
          <div className="row g-2 mb-2">
            <div className="col-12 col-md-4">
              <input
                className="form-control"
                placeholder="Fournisseur (ex. Caviste Rhône)"
                value={supplier}
                onChange={(e) => setSupplier(e.target.value)}
              />
            </div>
          </div>

          <div className="vstack gap-2">
            {lines.map((l, i) => (
              <POItemRow
                key={i}
                index={i}
                value={l}
                onChange={(nv) => setLineAt(i, nv)}
                onRemove={() => removeLine(i)}
              />
            ))}
          </div>

          <div className="d-flex flex-wrap align-items-center gap-3 mt-3">
            <button className="btn btn-outline-secondary" onClick={addLine}>
              + ligne
            </button>
            <div className="ms-auto">
              Total PO : <strong>{totalAmount.toLocaleString()}</strong>
            </div>
            <button className="btn btn-dark" disabled={creating} onClick={onCreate}>
              {creating ? "Création…" : "Créer le PO"}
            </button>
          </div>
        </div>

        {/* Liste des PO */}
        <div className="card">
          <div className="d-flex justify-content-between align-items-center p-3">
            <h6 className="m-0">Purchase Orders</h6>
            <button
              className="btn btn-outline-primary"
              disabled={!selected.size}
              onClick={goShipMulti}
            >
              Shipping groupé ({selected.size})
            </button>
          </div>

          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <input type="checkbox" checked={!!allChecked} onChange={toggleAll} />
                  </th>
                  <th>PO</th>
                  <th>Fournisseur</th>
                  <th>Statut</th>
                  <th>Progression</th>
                  <th>Montant</th>
                  <th>Créé</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pos.map(p => {
                  const checked = selected.has(p.id);
                  // ... tes helpers getTotals / badgeClass comme avant
                  return (
                    <tr key={p.id}>
                      <td>
                        <input type="checkbox" checked={checked} onChange={() => toggle(p.id)} />
                      </td>
                      <td>#{p.id.slice(0, 6)}</td>
                      <td>{p.supplier || "-"}</td>
                      <td><span className={badgeClass(p.status)}>{p.status || "open"}</span></td>
                      <td>{/* barre de progression comme avant */}</td>
                      <td>{Number(p.amount || 0).toLocaleString()}</td>
                      <td>{p.createdAt?.toDate?.().toLocaleString?.() || ""}</td>
                      <td className="text-end">
                        <div className="btn-group">
                          <Link className="btn btn-sm btn-outline-secondary" to={`/admin/po/${p.id}/edit`}>Éditer</Link>
                          <Link className="btn btn-sm btn-outline-primary" to={`/admin/po/${p.id}/ship`}>Shipping</Link>
                          <Link className="btn btn-sm btn-outline-dark" to={`/admin/shipments?po=${p.id}`}>Réceptions</Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!pos.length && (
                  <tr><td colSpan={8} className="text-muted">Aucun PO</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-muted small mt-3">
          La progression s’appuie sur <code>items[].qtyOrdered</code> / <code>qtyReceived</code> du PO.
          Les expéditions (shippings) sont créées depuis “Shipping”, puis réceptionnées
          sur la page de réception d’un <em>shipment</em> — ce qui met à jour le PO et le stock.
        </p>
      </div>
    </>
  );
}
